import { useTranslation } from 'react-i18next';
import { useAssetLibraryStore, type GizmoMode } from '@stores/assetLibraryStore';
import { MtTooltip } from '@components/MtTooltip';

interface GizmoToolbarProps {
  disabled?: boolean;
}

interface GizmoButton {
  mode: GizmoMode;
  icon: React.ReactNode;
}

const GIZMO_BUTTONS: GizmoButton[] = [
  {
    mode: 'translate',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
      >
        {/* 移动 gizmo: 三轴箭头简化 */}
        <path d="M12 2v20" />
        <path d="M12 2l-3 3M12 2l3 3" />
        <path d="M12 22l-3-3M12 22l3-3" />
        <path d="M2 12h20" />
        <path d="M2 12l3-3M2 12l3 3" />
        <path d="M22 12l-3-3M22 12l-3 3" />
      </svg>
    ),
  },
  {
    mode: 'rotate',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
      >
        {/* 旋转 gizmo: 圆弧+箭头 */}
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <path d="M21 3v5h-5" />
      </svg>
    ),
  },
  {
    mode: 'scale',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
      >
        {/* 缩放 gizmo: 方块+对角箭头 */}
        <rect x="8" y="8" width="8" height="8" rx="1" />
        <path d="M3 3h5M3 3v5" />
        <path d="M21 21h-5M21 21v-5" />
        <path d="M3 3l5 5" />
        <path d="M21 21l-5-5" />
      </svg>
    ),
  },
];

export function GizmoToolbar({ disabled }: GizmoToolbarProps) {
  const { t } = useTranslation();
  const gizmoMode = useAssetLibraryStore((s) => s.gizmoMode);
  const setGizmoMode = useAssetLibraryStore((s) => s.setGizmoMode);
  const selectedAssetId = useAssetLibraryStore((s) => s.selectedAssetId);

  const isDisabled = disabled || !selectedAssetId;

  return (
    <div className="pointer-events-auto flex flex-col gap-1.5 rounded-2xl bg-white/5 p-1.5 backdrop-blur-sm backdrop-brightness-30">
      {GIZMO_BUTTONS.map((btn) => {
        const active = gizmoMode === btn.mode;
        return (
          <MtTooltip key={btn.mode} content={t(`assetLibrary.${btn.mode}`)} placement="left">
            <button
              disabled={isDisabled}
              onClick={() => setGizmoMode(btn.mode)}
              aria-label={t(`assetLibrary.${btn.mode}`)}
              className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                active
                  ? 'bg-white/15 text-white'
                  : 'text-white/50 hover:bg-white/10 hover:text-white/70'
              } ${isDisabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}`}
            >
              {btn.icon}
            </button>
          </MtTooltip>
        );
      })}
    </div>
  );
}
